import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Building2, Mail, Phone, MapPin, CheckCircle, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useActiveClinic } from '@/lib/ActiveClinicContext';
import { useAuth } from '@/lib/auth';

interface CreateSubClinicModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentClinicType?: 'psychologist' | 'dentist' | 'specialist' | 'emdr_therapist';
  onSuccess?: (clinicId: string) => void;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  cvr: string;
  clinicType: 'psychologist' | 'dentist' | 'specialist' | 'emdr_therapist';
  address: {
    street: string;
    city: string;
    zipCode: string;
    country: string;
  };
}

export function CreateSubClinicModal({ 
  isOpen, 
  onClose, 
  parentClinicType = 'psychologist',
  onSuccess 
}: CreateSubClinicModalProps) {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { refreshSession } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    cvr: '',
    clinicType: parentClinicType,
    address: {
      street: '',
      city: '',
      zipCode: '',
      country: 'Danmark',
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!activeClinicId) throw new Error('No active clinic');
      
      const { data: result, error } = await supabase.rpc('create_sub_clinic', {
        p_parent_clinic_id: activeClinicId,
        p_name: data.name,
        p_email: data.email,
        p_phone: data.phone,
        p_address: data.address as any,
        p_clinic_type: data.clinicType,
        p_cvr: data.cvr || undefined,
      });
      if (error) throw error;
      return result;
    },
    onSuccess: async (clinicId) => {
      // Refresh JWT to get updated clinic memberships
      await refreshSession();
      
      queryClient.invalidateQueries({ queryKey: ['sub-clinics'] });
      if (typeof clinicId === 'string') {
        onSuccess?.(clinicId);
      }
      onClose();
      resetForm();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      cvr: '',
      clinicType: parentClinicType,
      address: { street: '', city: '', zipCode: '', country: 'Danmark' },
    });
    setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    createMutation.mutate(formData);
  };

  if (!isOpen) return null;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <form onSubmit={handleSubmit}>
                  <div className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                          <Building2 className="h-6 w-6 mr-3 text-indigo-600" />
                          {t('settings.clinics.modal.create_title')}
                        </Dialog.Title>
                        <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {t('settings.clinics.modal.create_description')}
                        </Dialog.Description>
                      </div>
                      <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-1 rounded-md"
                      >
                        <span className="sr-only">Close</span>
                        <X className="h-6 w-6" />
                      </button>
                    </div>

                    <div className="mt-6 space-y-6">
                      {error && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                        </div>
                      )}

                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          <Building2 className="h-4 w-4 inline mr-2" />
                          {t('settings.clinics.modal.clinic_name')} *
                        </label>
                        <input
                          type="text"
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="block w-full rounded-xl border border-gray-300 dark:border-gray-600 
                            bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                            placeholder-gray-400 focus:border-indigo-500 focus:outline-none 
                            focus:ring-indigo-500 sm:text-sm"
                          placeholder={t('settings.clinics.modal.clinic_name_placeholder')}
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          <Mail className="h-4 w-4 inline mr-2" />
                          {t('settings.clinics.modal.email')} *
                        </label>
                        <input
                          type="email"
                          id="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="block w-full rounded-xl border border-gray-300 dark:border-gray-600 
                            bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                            placeholder-gray-400 focus:border-indigo-500 focus:outline-none 
                            focus:ring-indigo-500 sm:text-sm"
                          placeholder={t('settings.clinics.modal.email_placeholder')}
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          <Phone className="h-4 w-4 inline mr-2" />
                          {t('settings.clinics.modal.phone')} *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="block w-full rounded-xl border border-gray-300 dark:border-gray-600 
                            bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                            placeholder-gray-400 focus:border-indigo-500 focus:outline-none 
                            focus:ring-indigo-500 sm:text-sm"
                          placeholder={t('settings.clinics.modal.phone_placeholder')}
                        />
                      </div>

                      <div>
                        <label htmlFor="cvr" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          {t('settings.clinics.modal.cvr')}
                        </label>
                        <input
                          type="text"
                          id="cvr"
                          value={formData.cvr}
                          onChange={(e) => setFormData({ ...formData, cvr: e.target.value })}
                          className="block w-full rounded-xl border border-gray-300 dark:border-gray-600 
                            bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                            placeholder-gray-400 focus:border-indigo-500 focus:outline-none 
                            focus:ring-indigo-500 sm:text-sm"
                          placeholder={t('settings.clinics.modal.cvr_placeholder')}
                        />
                      </div>

                      <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
                        <label htmlFor="clinicType" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          <CheckCircle className="h-4 w-4 inline mr-2 text-indigo-600" />
                          {t('settings.clinics.modal.clinic_type')}
                        </label>
                        <select
                          id="clinicType"
                          value={formData.clinicType}
                          onChange={(e) => setFormData({ ...formData, clinicType: e.target.value as FormData['clinicType'] })}
                          className="block w-full rounded-xl border border-gray-300 dark:border-gray-600 
                            bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                            placeholder-gray-400 focus:border-indigo-500 focus:outline-none 
                            focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="psychologist">{t('settings.clinics.modal.type_psychologist')}</option>
                          <option value="dentist">{t('settings.clinics.modal.type_dentist')}</option>
                          <option value="specialist">{t('settings.clinics.modal.type_specialist')}</option>
                          <option value="emdr_therapist">{t('settings.clinics.modal.type_emdr_therapist')}</option>
                        </select>
                      </div>

                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                          {t('settings.clinics.modal.address_title')}
                        </h4>
                        
                        <div>
                          <label htmlFor="street" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            {t('settings.clinics.modal.street')}
                          </label>
                          <input
                            type="text"
                            id="street"
                            required
                            value={formData.address.street}
                            onChange={(e) => setFormData({
                              ...formData,
                              address: { ...formData.address, street: e.target.value }
                            })}
                            className="block w-full rounded-xl border border-gray-300 dark:border-gray-600 
                              bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                              placeholder-gray-400 focus:border-indigo-500 focus:outline-none 
                              focus:ring-indigo-500 sm:text-sm"
                            placeholder={t('settings.clinics.modal.street_placeholder')}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                              {t('settings.clinics.modal.zip_code')}
                            </label>
                            <input
                              type="text"
                              id="zipCode"
                              required
                              value={formData.address.zipCode}
                              onChange={(e) => setFormData({
                                ...formData,
                                address: { ...formData.address, zipCode: e.target.value }
                              })}
                              className="block w-full rounded-xl border border-gray-300 dark:border-gray-600 
                                bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                                placeholder-gray-400 focus:border-indigo-500 focus:outline-none 
                                focus:ring-indigo-500 sm:text-sm"
                              placeholder={t('settings.clinics.modal.zip_code_placeholder')}
                            />
                          </div>

                          <div>
                            <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                              {t('settings.clinics.modal.city_label')}
                            </label>
                            <input
                              type="text"
                              id="city"
                              required
                              value={formData.address.city}
                              onChange={(e) => setFormData({
                                ...formData,
                                address: { ...formData.address, city: e.target.value }
                              })}
                              className="block w-full rounded-xl border border-gray-300 dark:border-gray-600 
                                bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                                placeholder-gray-400 focus:border-indigo-500 focus:outline-none 
                                focus:ring-indigo-500 sm:text-sm"
                              placeholder={t('settings.clinics.modal.city_placeholder')}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="submit"
                      disabled={createMutation.isPending}
                      className="inline-flex w-full justify-center items-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm 
                        font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 
                        focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed 
                        sm:ml-3 sm:w-auto"
                    >
                      {createMutation.isPending ? (
                        <>
                          <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                          {t('settings.clinics.modal.creating')}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {t('settings.clinics.modal.create_button')}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={createMutation.isPending}
                      className="mt-3 inline-flex w-full justify-center items-center rounded-xl bg-white dark:bg-gray-600 px-4 
                        py-2.5 text-sm font-semibold text-gray-900 dark:text-gray-200 shadow-sm ring-1 
                        ring-inset ring-gray-300 dark:ring-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 
                        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 
                        disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
                    >
                      <X className="h-4 w-4 mr-2" />
                      {t('settings.clinics.modal.cancel')}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
